import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemButton,
  Divider,
  Paper,
  LinearProgress,
  Fade,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Zoom,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Queue as QueueIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Call as CallIcon,
  CallEnd as CallEndIcon,
  PhoneCallback as PhoneCallbackIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  QueueMusic as QueueMusicIcon,
  LocationOn as LocationOnIcon,
  PhoneInTalk as PhoneInTalkIcon,
  PhoneMissed as PhoneMissedIcon,
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useLocation } from '../contexts/LocationContext';
import { useQueue } from '../contexts/QueueContext';
import { useSipNumber } from '../contexts/SipNumberContext';
import { useUser } from '../contexts/UserContext';
import { useReports } from '../contexts/ReportsContext';
import authService from '../services/authService';
import {
  Assignment as AssignmentIcon
} from '@mui/icons-material';

// Sayfa bileşenlerini import et
import UserManagement from './UserManagement';
import SipManagement from './SipManagement';
import QueueManagement from './QueueManagement';
import CallManagement from './CallManagement';

// Dashboard data interface
interface DashboardData {
  dailyStats: {
    totalCalls: number;
    outgoingCalls: number;
    incomingCalls: number;
    missedCalls: number;
  };
  realTimeStats: {
    activeCalls: number;
    waitingCalls: number;
    avgTalkTime: number;
    avgWaitTime: number;
  };
  agents: Array<{
    name: string;
    status: string;
    statusTime: string;
    outgoingCalls: number;
    incomingCalls: number;
  }>;
  waitingCalls: Array<{
    caller: string;
    queue: string;
    waitTime: string;
    priority: string;
  }>;
  queues: Array<{
    name: string;
    totalCalls: number;
    answered: number;
    missed: number;
    avgWaitTime: number;
    agents: number;
  }>;
  hourlyCalls: Array<{
    hour: string;
    total: number;
    answered: number;
    missed: number;
  }>;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'warning' | 'info' });
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // Context hooks
  const locationContext = useLocation();
  const queueContext = useQueue();
  const sipNumberContext = useSipNumber();
  const userContext = useUser();
  const reportsContext = useReports();

  // Get current user
  const currentUser = authService.getCurrentUser();

  // Load dashboard data from API
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [
        dailyStats,
        realTimeStats,
        activeAgents,
        waitingCalls,
        queuePerformance,
        hourlyDistribution
      ] = await Promise.all([
        reportsContext.fetchDailyStats(),
        reportsContext.fetchRealtimeStats(),
        reportsContext.fetchActiveAgents(),
        reportsContext.fetchWaitingCalls(),
        reportsContext.fetchQueuePerformance(),
        reportsContext.fetchHourlyDistribution()
      ]);

      // Transform data to match interface
      const transformedData: DashboardData = {
        dailyStats: {
          totalCalls: dailyStats.totalCalls || 0,
          outgoingCalls: dailyStats.outgoingCalls || 0,
          incomingCalls: dailyStats.incomingCalls || 0,
          missedCalls: dailyStats.missedCalls || 0
        },
        realTimeStats: {
          activeCalls: realTimeStats.activeCalls || 0,
          waitingCalls: realTimeStats.waitingCalls || 0,
          avgTalkTime: realTimeStats.avgTalkTime || 0,
          avgWaitTime: realTimeStats.avgWaitTime || 0
        },
        agents: activeAgents.map(agent => ({
          name: agent.name,
          status: agent.status,
          statusTime: agent.statusTime,
          outgoingCalls: agent.outgoingCalls || 0,
          incomingCalls: agent.incomingCalls || 0
        })),
        waitingCalls: waitingCalls.map(call => ({
          caller: call.phoneNumber,
          queue: call.queueName,
          waitTime: call.waitTime,
          priority: call.priority
        })),
        queues: queuePerformance.map(queue => ({
          name: queue.name,
          totalCalls: queue.totalCalls || 0,
          answered: queue.answered || 0,
          missed: queue.missed || 0,
          avgWaitTime: queue.avgWaitTime || 0,
          agents: queue.agentCount || 0
        })),
        hourlyCalls: hourlyDistribution.map(hour => ({
          hour: hour.hour,
          total: hour.total || 0,
          answered: hour.answered || 0,
          missed: hour.missed || 0
        }))
      };

      setDashboardData(transformedData);
    } catch (error) {
      console.error('Dashboard verileri yüklenirken hata:', error);
      setSnackbar({
        open: true,
        message: 'Dashboard verileri yüklenirken hata oluştu',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    loadDashboardData();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#F5F5F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Show error state if no data
  if (!dashboardData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#F5F5F5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6" color="error">
          Dashboard verileri yüklenemedi
        </Typography>
        <Button variant="contained" onClick={loadDashboardData}>
          Tekrar Dene
        </Button>
      </Box>
    );
  }

  // Refresh data
  const refreshData = async () => {
    await loadDashboardData();
    setSnackbar({
      open: true,
      message: 'Veriler başarıyla yenilendi',
      severity: 'success'
    });
  };

  // Show snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get dashboard statistics from context
  const dashboardStats = {
    totalUsers: userContext.users.length,
    activeUsers: userContext.users.filter(user => user.status === 'active').length,
    totalQueues: queueContext.queues.length,
    activeQueues: queueContext.queues.filter(queue => queue.isActive).length,
    totalCalls: dashboardData?.dailyStats.totalCalls || 0,
    todayCalls: dashboardData?.dailyStats.totalCalls || 0,
    avgCallDuration: dashboardData?.realTimeStats.avgTalkTime || 0,
    successRate: dashboardData ? ((dashboardData.dailyStats.totalCalls - dashboardData.dailyStats.missedCalls) / dashboardData.dailyStats.totalCalls * 100) : 0
  };

  const recentActivities = [
    { id: 1, type: 'user', action: 'Yeni kullanıcı eklendi', user: 'Ahmet Yılmaz', time: '2 dakika önce', icon: PersonAddIcon },
    { id: 2, type: 'queue', action: 'Kuyruk güncellendi', user: 'Satış Kuyruğu', time: '5 dakika önce', icon: QueueMusicIcon },
    { id: 3, type: 'call', action: 'Çağrı kaydedildi', user: '0538 123 45 67', time: '8 dakika önce', icon: CallIcon },
    { id: 4, type: 'sip', action: 'SIP numarası eklendi', user: '+90 212 555 0123', time: '12 dakika önce', icon: PhoneIcon },
    { id: 5, type: 'user', action: 'Kullanıcı yetkisi güncellendi', user: 'Mehmet Kaya', time: '15 dakika önce', icon: AdminIcon }
  ];

  const quickActions = [
    { title: 'Kullanıcı Ekle', icon: PersonAddIcon, color: '#1976D2', path: '/user-management' },
    { title: 'SIP Ekle', icon: PhoneIcon, color: '#4CAF50', path: '/sip-management' },
    { title: 'Kuyruk Oluştur', icon: QueueMusicIcon, color: '#FF9800', path: '/queue-management' },
    { title: 'Lokasyon Ekle', icon: LocationOnIcon, color: '#795548', path: '/location-management' },
    { title: 'IVR Yönetimi', icon: PhoneIcon, color: '#E91E63', path: '/ivr-management' },
    { title: 'IVR Oluşturucu', icon: SettingsIcon, color: '#FF5722', path: '/ivr-builder' },
    { title: 'Çağrıları İndir', icon: DownloadIcon, color: '#9C27B0', path: '/call-recordings' },
    { title: 'Raporlar', icon: AssessmentIcon, color: '#F44336', path: '/dashboard' },
    { title: 'Ayarlar', icon: SettingsIcon, color: '#607D8B', path: '/admin/settings' }
  ];

  const menuItems = [
    { id: 'overview', label: 'Genel Bakış', icon: DashboardIcon },
    { id: 'users', label: 'Kullanıcılar', icon: PeopleIcon },
    { id: 'sip', label: 'SIP Numaraları', icon: PhoneIcon },
    { id: 'queues', label: 'Kuyruklar', icon: QueueIcon },
    { id: 'calls', label: 'Çağrılar', icon: CallIcon },
    { id: 'reports', label: 'Raporlar', icon: AssessmentIcon },
    { id: 'settings', label: 'Ayarlar', icon: SettingsIcon }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'users':
        return <UserManagement />;
      case 'sip':
        return <SipManagement />;
      case 'queues':
        return <QueueManagement />;
      case 'calls':
        return <CallManagement />;
      case 'reports':
        return renderReports();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'busy': return 'warning';
      case 'away': return 'info';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'busy': return 'Meşgul';
      case 'away': return 'Uzakta';
      default: return 'Offline';
    }
  };

  const renderOverview = () => {
    // Show loading if data is being fetched
    if (reportsContext.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    // Show error if there's an error
    if (reportsContext.error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reportsContext.error}
        </Alert>
      );
    }

    return (
    <Box sx={{ p: 3, width: '100%', height: '100vh', overflow: 'auto' }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Admin Paneli - Genel Bakış
      </Typography>

      {/* Günlük İstatistikler */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Günlük İstatistikler
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs:7, md: 4, lg: 1 }}>
          <Zoom in timeout={500}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.dailyStats.totalCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Toplam Çağrı
                    </Typography>
                  </Box>
                  <PhoneIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={700}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.dailyStats.outgoingCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Giden Arama
                    </Typography>
                  </Box>
                  <PhoneCallbackIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={900}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.dailyStats.incomingCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Gelen Arama
                    </Typography>
                  </Box>
                  <PhoneInTalkIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1100}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #F44336 0%, #D32F2F 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.dailyStats.missedCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Cevapsız Çağrı
                    </Typography>
                  </Box>
                  <PhoneMissedIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>
      </Grid2>

      {/* Gerçek Zamanlı İstatistikler */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Gerçek Zamanlı İstatistikler
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1300}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.realTimeStats.activeCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Aktif Çağrı
                    </Typography>
                  </Box>
                  <PhoneInTalkIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1500}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.realTimeStats.waitingCalls || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Bekleyen Çağrı
                    </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1700}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.realTimeStats.avgTalkTime || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Ort. Konuşma (dk)
                    </Typography>
                  </Box>
                  <AccessTimeIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1900}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #795548 0%, #5D4037 100%)',
              color: 'white',
              height: '140px',
              minHeight: '140px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', mb: 1 }}>
                      {dashboardData?.realTimeStats.avgWaitTime || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                      Ort. Bekleme (dk)
                    </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 50, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>
      </Grid2>

      {/* Aktif Temsilciler ve Bekleyen Çağrılar */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Aktif Temsilciler ve Bekleyen Çağrılar
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        {/* Aktif Temsilciler Tablosu */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Zoom in timeout={2100}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
              height: '500px',
              minHeight: '500px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Aktif Temsilciler
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <TableContainer>
                    <Table stickyHeader sx={{ minWidth: 600, width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '40%'
                          }}>
                            Temsilci
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Durum
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Süre
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Çağrı
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardData?.agents.map((agent, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: getStatusColor(agent.status) + '.main' }}>
                                  {agent.name ? agent.name.split(' ').map(n => n[0]).join('') : '??'}
                                </Avatar>
                                <Typography variant="body1" fontWeight="600" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>
                                  {agent.name || 'Bilinmeyen'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Chip
                                label={getStatusText(agent.status)}
                                size="small"
                                color={getStatusColor(agent.status)}
                                variant="filled"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {agent.statusTime}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="#1976D2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {agent.outgoingCalls + agent.incomingCalls}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>

        {/* Bekleyen Çağrılar Tablosu */}
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Zoom in timeout={2300}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
              height: '500px',
              minHeight: '500px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Bekleyen Çağrılar
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <TableContainer>
                    <Table stickyHeader sx={{ minWidth: 600, width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '40%'
                          }}>
                            Arayan
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Kuyruk
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Bekleme
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '20%',
                            textAlign: 'center'
                          }}>
                            Öncelik
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardData?.waitingCalls.map((call, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ bgcolor: '#E3F2FD', width: 32, height: 32 }}>
                                  <PhoneIcon sx={{ color: '#1976D2', fontSize: 16 }} />
                                </Avatar>
                                <Typography variant="body1" fontWeight="600" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>
                                  {call.caller}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Chip
                                label={call.queue}
                                size="small"
                                color="primary"
                                variant="filled"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {call.waitTime}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Chip
                                label={call.priority}
                                size="small"
                                color={call.priority === 'Yüksek' ? 'error' : call.priority === 'Normal' ? 'warning' : 'default'}
                                variant="filled"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>
      </Grid2>

      {/* Kuyruk Performansı */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Kuyruk Performansı
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12 }}>
          <Zoom in timeout={2500}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              height: '500px',
              minHeight: '500px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Kuyruk Performansı
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  <TableContainer>
                    <Table stickyHeader sx={{ minWidth: 800 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '25%',
                            minWidth: 150
                          }}>
                            Kuyruk
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '15%',
                            minWidth: 100,
                            textAlign: 'center'
                          }}>
                            Toplam Çağrı
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '15%',
                            minWidth: 100,
                            textAlign: 'center'
                          }}>
                            Cevaplanan
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '15%',
                            minWidth: 100,
                            textAlign: 'center'
                          }}>
                            Cevapsız
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '15%',
                            minWidth: 120,
                            textAlign: 'center'
                          }}>
                            Ort. Bekleme
                          </TableCell>
                          <TableCell sx={{ 
                            fontWeight: 'bold', 
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '0.875rem',
                            backgroundColor: '#f5f5f5',
                            width: '15%',
                            minWidth: 100,
                            textAlign: 'center'
                          }}>
                            Temsilci
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dashboardData?.queues.map((queue, index) => (
                          <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                            <TableCell sx={{ width: '25%', minWidth: 150 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <QueueIcon sx={{ color: '#1976D2', fontSize: 24 }} />
                                <Typography variant="body1" fontWeight="600" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>
                                  {queue.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ width: '15%', minWidth: 100, textAlign: 'center' }}>
                              <Typography variant="body2" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {queue.totalCalls}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '15%', minWidth: 100, textAlign: 'center' }}>
                              <Typography variant="body2" color="#4CAF50" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {queue.answered}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '15%', minWidth: 100, textAlign: 'center' }}>
                              <Typography variant="body2" color="#F44336" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {queue.missed}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '15%', minWidth: 120, textAlign: 'center' }}>
                              <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {queue.avgWaitTime} dk
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '15%', minWidth: 100, textAlign: 'center' }}>
                              <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.875rem' }}>
                                {queue.agents}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>
      </Grid2>

      {/* Saatlik Çağrı Dağılımı */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Saatlik Çağrı Dağılımı
      </Typography>
      <Grid2 container spacing={3} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12 }}>
          <Zoom in timeout={2700}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              height: '400px',
              minHeight: '400px'
            }}>
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" fontWeight="bold" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                  Saatlik Çağrı Dağılımı
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'end', gap: 2, height: 250, mb: 3 }}>
                    {dashboardData?.hourlyCalls.map((hour, index) => (
                      <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Box
                            sx={{
                              width: '100%',
                              height: `${(hour.total / 200) * 200}px`,
                              bgcolor: '#1976D2',
                              borderRadius: '6px 6px 0 0',
                              position: 'relative',
                              minHeight: '20px',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: `${(hour.answered / hour.total) * 100}%`,
                                bgcolor: '#4CAF50',
                                borderRadius: '6px 6px 0 0',
                              }
                            }}
                          />
                        </Box>
                        <Typography variant="body2" color="#666" sx={{ fontSize: '0.8rem', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                          {hour.hour}
                        </Typography>
                        <Typography variant="h6" color="#2C3E50" sx={{ fontSize: '0.9rem', fontWeight: 'bold', fontFamily: 'Inter, sans-serif' }}>
                          {hour.total}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: '#4CAF50', borderRadius: 2 }} />
                      <Typography variant="h6" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                        Cevaplanan
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, bgcolor: '#1976D2', borderRadius: 2 }} />
                      <Typography variant="h6" color="#666" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                        Toplam
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid2>
      </Grid2>

      {/* Hızlı İşlemler */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Hızlı İşlemler
      </Typography>
      <Grid2 container spacing={2} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #E0E0E0',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => navigate(action.path)}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: action.color, width: 40, height: 40 }}>
                    <action.icon sx={{ color: 'white' }} />
                  </Avatar>
                  <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif' }}>
                    {action.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        ))}
      </Grid2>

      {/* Son Aktiviteler */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Son Aktiviteler
      </Typography>
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <List>
            {recentActivities.map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem sx={{ px: 3, py: 2 }}>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: '#F5F5F5', width: 32, height: 32 }}>
                      <activity.icon sx={{ color: '#666', fontSize: 18 }} />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {activity.action}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {activity.user} • {activity.time}
                      </Typography>
                    }
                  />
                </ListItem>
                {index < recentActivities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
        </Card>
      </Box>
    );
  };

  const renderReports = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Raporlar ve Analizler
      </Typography>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Günlük Çağrı Raporu
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Bugünkü çağrı istatistikleri ve performans metrikleri
              </Typography>
              <Button variant="contained" sx={{ bgcolor: '#1976D2', fontFamily: 'Inter, sans-serif' }}>
                Raporu Görüntüle
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Temsilci Performans Raporu
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Temsilci bazında detaylı performans analizi
              </Typography>
              <Button variant="contained" sx={{ bgcolor: '#4CAF50', fontFamily: 'Inter, sans-serif' }}>
                Raporu Görüntüle
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Kuyruk Analiz Raporu
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Kuyruk performansı ve bekleme süreleri analizi
              </Typography>
              <Button variant="contained" sx={{ bgcolor: '#FF9800', fontFamily: 'Inter, sans-serif' }}>
                Raporu Görüntüle
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Aylık Özet Raporu
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Aylık genel performans ve trend analizi
              </Typography>
              <Button variant="contained" sx={{ bgcolor: '#9C27B0', fontFamily: 'Inter, sans-serif' }}>
                Raporu Görüntüle
              </Button>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderSettings = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Sistem Ayarları
      </Typography>

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Genel Ayarlar
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Sistem geneli ayarlar ve konfigürasyonlar
              </Typography>
              <Button variant="outlined" sx={{ fontFamily: 'Inter, sans-serif' }}>
                Ayarları Düzenle
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                SIP Ayarları
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                SIP sunucu ve protokol ayarları
              </Typography>
              <Button variant="outlined" sx={{ fontFamily: 'Inter, sans-serif' }}>
                SIP Ayarları
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Kayıt Ayarları
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Çağrı kayıt ayarları ve depolama konfigürasyonu
              </Typography>
              <Button variant="outlined" sx={{ fontFamily: 'Inter, sans-serif' }}>
                Kayıt Ayarları
              </Button>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Güvenlik Ayarları
              </Typography>
              <Typography variant="body2" color="#666" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
                Kullanıcı yetkileri ve güvenlik politikaları
              </Typography>
              <Button variant="outlined" sx={{ fontFamily: 'Inter, sans-serif' }}>
                Güvenlik Ayarları
              </Button>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#F5F5F5' }}>
      {/* Sol Menü */}
      <Box sx={{
        width: 280,
        bgcolor: 'white',
        borderRight: '1px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid #E0E0E0', bgcolor: '#2C3E50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AdminIcon sx={{ color: 'white', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="700" color="white" sx={{ fontFamily: 'Inter, sans-serif' }}>
              Admin Panel
            </Typography>
          </Box>
        </Box>

        {/* Menü */}
        <Box sx={{ flex: 1, p: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => setActiveTab(item.id)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: activeTab === item.id ? '#E3F2FD' : 'transparent',
                    color: activeTab === item.id ? '#1976D2' : '#666',
                    '&:hover': {
                      bgcolor: activeTab === item.id ? '#E3F2FD' : '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <item.icon sx={{ 
                      color: activeTab === item.id ? '#1976D2' : '#666',
                      fontSize: 20
                    }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body1" fontWeight={activeTab === item.id ? 600 : 400} sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Alt Bilgi */}
        <Box sx={{ p: 3, borderTop: '1px solid #E0E0E0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: '#2C3E50', width: 40, height: 40 }}>
              A
            </Avatar>
            <Box>
              <Typography variant="body1" fontWeight="600" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif' }}>
                Admin User
              </Typography>
              <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                Sistem Yöneticisi
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Fade in timeout={300}>
          {renderContent()}
        </Fade>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
